import React, { useState, useEffect } from 'react';
import { Modal, View, Text, TextInput, Pressable, StyleSheet, ScrollView } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';

interface EditFieldModalProps {
    visible: boolean;
    onClose: () => void;
    onSave: (value: string) => void | Promise<void>;
    field: {
        label: string;
        value: string;
        placeholder: string;
        keyboardType?: 'default' | 'email-address' | 'phone-pad' | 'numeric' | 'number-pad';
        multiline?: boolean;
        maxLength?: number;
    };
    saveButtonText?: string;
}

const EditFieldModal: React.FC<EditFieldModalProps> = ({
    visible,
    onClose,
    onSave,
    field,
    saveButtonText = 'Save',
}) => {
    const [value, setValue] = useState(field.value);

    useEffect(() => {
        setValue(field.value);
    }, [field.value]);

    const formatPhoneNumber = (phone: string) => {
        // Remove all non-digits
        const digits = phone.replace(/\D/g, '');

        // Format as XXX-XXX-XXXX
        if (digits.length <= 3) return digits;
        if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
        return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    };

    const handlePhoneChange = (text: string) => {
        // Only allow digits and dashes
        const cleaned = text.replace(/[^\d-]/g, '');
        const formatted = formatPhoneNumber(cleaned);
        setValue(formatted);
    };

    const handleSave = async () => {
        try {
            await Promise.resolve(onSave(value));
        } finally {
            // Only close modal for non-phone fields
            // Phone field will be closed by parent after verification flow starts
            if (field.label !== 'Phone Number') {
                onClose();
            }
        }
    };

    const handleCancel = () => {
        setValue(field.value); // Reset to original value
        onClose();
    };

    return (
        <Modal
            visible={visible}
            animationType="slide"
            transparent={true}
            onRequestClose={handleCancel}
        >
            <View style={styles.modalOverlay}>
                <View style={styles.modalContainer}>
                    {/* Header */}
                    <View style={styles.header}>
                        <Text style={styles.headerTitle}>Edit {field.label}</Text>
                        <Pressable onPress={handleCancel} style={styles.closeButton}>
                            <MaterialCommunityIcons name="close" size={24} color="#666" />
                        </Pressable>
                    </View>

                    {/* Content */}
                    <View style={styles.content}>
                        <Text style={styles.label}>{field.label}</Text>
                        <TextInput
                            style={[
                                styles.input,
                                field.multiline && styles.multilineInput
                            ]}
                            value={value}
                            onChangeText={field.keyboardType === 'phone-pad' ? handlePhoneChange : setValue}
                            placeholder={field.placeholder}
                            placeholderTextColor="#999"
                            keyboardType={field.keyboardType || 'default'}
                            multiline={field.multiline}
                            numberOfLines={field.multiline ? 4 : 1}
                            maxLength={field.maxLength}
                            autoFocus={true}
                        />
                        {field.maxLength && (
                            <Text style={styles.characterCount}>
                                {value.length}/{field.maxLength}
                            </Text>
                        )}
                    </View>

                    {/* Footer */}
                    <View style={styles.footer}>
                        <Pressable style={styles.cancelButton} onPress={handleCancel}>
                            <Text style={styles.cancelButtonText}>Cancel</Text>
                        </Pressable>
                        <Pressable style={styles.saveButton} onPress={handleSave}>
                            <Text style={styles.saveButtonText}>{saveButtonText}</Text>
                        </Pressable>
                    </View>
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(15, 23, 42, 0.6)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    modalContainer: {
        backgroundColor: '#fff',
        borderRadius: 32,
        width: '100%',
        maxWidth: 480,
        maxHeight: '90%',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 20 },
        shadowOpacity: 0.1,
        shadowRadius: 30,
        elevation: 15,
        overflow: 'hidden',
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 28,
        paddingVertical: 24,
        borderBottomWidth: 1,
        borderBottomColor: '#F1F5F9',
    },
    headerTitle: {
        fontSize: 22,
        fontWeight: '800',
        color: '#1E293B',
        letterSpacing: -0.5,
    },
    closeButton: {
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: '#F1F5F9',
        alignItems: 'center',
        justifyContent: 'center',
    },
    content: {
        padding: 28,
    },
    label: {
        fontSize: 12,
        fontWeight: '700',
        color: '#64748B',
        marginBottom: 10,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    input: {
        borderWidth: 2,
        borderColor: '#F1F5F9',
        borderRadius: 16,
        padding: 16,
        fontSize: 16,
        fontWeight: '500',
        color: '#1E293B',
        backgroundColor: '#F8FAFC',
    },
    multilineInput: {
        height: 120,
        textAlignVertical: 'top',
    },
    characterCount: {
        fontSize: 12,
        color: '#94A3B8',
        textAlign: 'right',
        marginTop: 8,
        fontWeight: '600',
    },
    footer: {
        flexDirection: 'row',
        paddingHorizontal: 28,
        paddingVertical: 24,
        backgroundColor: '#F8FAFC',
        borderTopWidth: 1,
        borderTopColor: '#F1F5F9',
        gap: 16,
    },
    cancelButton: {
        flex: 1,
        paddingVertical: 14,
        borderRadius: 16,
        backgroundColor: '#FFFFFF',
        borderWidth: 1,
        borderColor: '#E2E8F0',
        alignItems: 'center',
        justifyContent: 'center',
    },
    cancelButtonText: {
        fontSize: 15,
        fontWeight: '700',
        color: '#64748B',
    },
    saveButton: {
        flex: 1.5,
        paddingVertical: 14,
        borderRadius: 16,
        backgroundColor: '#3C8248',
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: '#3C8248',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.2,
        shadowRadius: 8,
    },
    saveButtonText: {
        fontSize: 15,
        fontWeight: '700',
        color: '#fff',
    },
});

export default EditFieldModal;
